import { useEffect, useRef, useState } from 'react';
import {
  dateInputToMillis,
  DueTone,
  formatDueDate,
  millisToDateInput,
} from '../../pm/dueDate';
import PortalMenu from './PortalMenu';

interface Props {
  dueDate?: number;
  taskDone: boolean;
  onChange: (dueDate: number | null) => void;
}

const DueDatePicker: React.FC<Props> = ({ dueDate, taskDone, onChange }) => {
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState<string>(dueDate ? millisToDateInput(dueDate) : '');
  // 1-minute re-render so "Due tomorrow" -> "Due today" naturally as midnight
  // rolls past, without keeping a global ticker.
  const [, tick] = useState(0);
  const triggerRef = useRef<HTMLSpanElement | null>(null);
  const inputRef = useRef<HTMLInputElement | null>(null);

  useEffect(() => {
    setDraft(dueDate ? millisToDateInput(dueDate) : '');
  }, [dueDate]);

  useEffect(() => {
    if (open && inputRef.current) {
      // Autofocus the date input so keyboard users can pick immediately.
      inputRef.current.focus();
    }
  }, [open]);

  useEffect(() => {
    const id = window.setInterval(() => tick((n) => n + 1), 60000);
    return () => window.clearInterval(id);
  }, []);

  function commit() {
    const ms = dateInputToMillis(draft);
    if (ms === null) return; // ignore invalid input
    onChange(ms);
    setOpen(false);
  }

  function clear() {
    setDraft('');
    onChange(null);
    setOpen(false);
  }

  const display = typeof dueDate === 'number'
    ? formatDueDate(dueDate, Date.now(), taskDone)
    : null;

  const pillToneClass = (tone: DueTone) => {
    switch (tone) {
      case 'overdue': return 'pm-due-overdue';
      case 'today':   return 'pm-due-today';
      case 'soon':    return 'pm-due-soon';
      case 'done':    return 'pm-due-done';
      default:        return 'pm-due-normal';
    }
  };

  return (
    <span className="pm-due-wrap" ref={triggerRef}>
      {display ? (
        <span
          className={`pm-pill pm-due ${pillToneClass(display.tone)}`}
          onClick={() => setOpen((v) => !v)}
          role="button"
          tabIndex={0}
          onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') setOpen((v) => !v); }}
          title="Click to change the due date"
        >
          {display.label}
        </span>
      ) : (
        <button
          type="button"
          className="pm-pill pm-due pm-due-empty"
          onClick={() => setOpen((v) => !v)}
          title="Set a due date"
        >
          + Date
        </button>
      )}
      <PortalMenu
        open={open}
        triggerRef={triggerRef}
        onDismiss={() => setOpen(false)}
        className="pm-due-menu"
        estimatedHeight={96}
      >
        <input
          ref={inputRef}
          type="date"
          className="pm-due-input"
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') { e.preventDefault(); commit(); }
            else if (e.key === 'Escape') { e.preventDefault(); setOpen(false); }
          }}
        />
        <div className="pm-due-menu-actions">
          {typeof dueDate === 'number' ? (
            <button type="button" className="pm-link-button pm-danger" onClick={clear}>
              Clear
            </button>
          ) : <span />}
          <button
            type="button"
            className="pm-button pm-button-primary pm-due-save"
            onClick={commit}
            disabled={!draft}
          >
            Save
          </button>
        </div>
      </PortalMenu>
    </span>
  );
};

export default DueDatePicker;
